import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useLang, translations } from "../i18n";

export function HowItWorksSection() {
  const { lang } = useLang();
  const t = translations.howItWorks;
  const stepsRef = useScrollReveal(100);
  const codeRef = useScrollReveal(0);

  return (
    <section
      id="how-it-works"
      className="bg-[var(--background)] py-20 md:py-28 lg:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-6xl mx-auto">
        <div ref={stepsRef} className="mb-16 md:mb-20">
          <span className="reveal inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--primary)] px-3 py-1.5 bg-[var(--primary)]/8 border border-[var(--primary)]/15 rounded-full mb-4">
            {t.badge[lang]}
          </span>
          <h2 className="reveal text-3xl md:text-[40px] font-extrabold tracking-tight text-[var(--foreground)] leading-[1.15] mb-12">
            {t.heading[lang]}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.steps.map((s, i) => (
              <div
                key={i}
                className="reveal flex flex-col gap-3 p-6 rounded-xl border border-[var(--border)] bg-[var(--card)]"
              >
                <span className="text-2xl font-black text-[var(--primary)] tracking-tight">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="text-[15px] font-bold text-[var(--foreground)]">
                  {s.title[lang]}
                </h3>
                <p className="text-sm text-[var(--muted-foreground)] leading-relaxed">
                  {s.description[lang]}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div ref={codeRef} className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <h3 className="reveal text-2xl md:text-3xl font-extrabold tracking-tight text-[var(--foreground)] leading-[1.15] mb-4">
              {t.codeHeading[lang]}
            </h3>
            <p className="reveal text-[15px] text-[var(--muted-foreground)] mb-6 max-w-md leading-relaxed">
              {t.codeDescription[lang]}
            </p>
            <div className="reveal flex flex-col gap-3">
              {t.apiBullets[lang].map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 text-sm text-[var(--muted-foreground)]"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--primary)]" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Code block — always in English (API content) */}
          <div className="reveal">
            <div className="bg-[var(--code-bg)] border border-[var(--border)] rounded-xl overflow-hidden shadow-xl">
              <div className="flex items-center gap-2 px-4 py-3 bg-black/20 border-b border-white/[0.06]">
                <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <span className="ml-3 font-mono text-[11px] font-medium text-white/40">
                  create-deposit.sh
                </span>
              </div>
              <div className="p-5 font-mono text-[13px] leading-[1.7] overflow-x-auto text-white">
                <div className="text-white/30">
                  curl -X POST https://api.vilverin.com/v1/ramp/deposits \
                </div>
                <div className="text-white/30">
                  {"  "}-H "Authorization: Bearer sk_live_..." \
                </div>
                <div className="text-white/30">
                  {"  "}-H "Content-Type: application/json" \
                </div>
                <div className="text-white/30">{"  "}-d '{"{"}'</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"amount_brl"</span>{": "}<span className="text-[#FDE68A]">100.00</span>,</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"lightning_invoice"</span>{": "}<span className="text-[#FDE68A]">"lnbc1..."</span>,</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"metadata"</span>{": "}<span className="text-white/50">{"{"}</span> <span className="text-[#A5B4FC]">"order_id"</span>{": "}<span className="text-[#FDE68A]">"ord_abc123"</span> <span className="text-white/50">{"}"}</span></div>
                <div className="text-white/30">{"  "}{"}"}'</div>
                <br />
                <div><span className="inline-block px-2 py-0.5 bg-[#86EFAC]/10 border border-[#86EFAC]/20 rounded text-[11px] text-[#86EFAC] font-medium">201 Created</span></div>
                <br />
                <div className="text-white/50">{"{"}</div>
                <div>{"  "}<span className="text-[#A5B4FC]">"id"</span>{": "}<span className="text-[#86EFAC]">"dep_7f3a..."</span>,</div>
                <div>{"  "}<span className="text-[#A5B4FC]">"status"</span>{": "}<span className="text-[#86EFAC]">"awaiting_payment"</span>,</div>
                <div>{"  "}<span className="text-[#A5B4FC]">"pix_qr_code"</span>{": "}<span className="text-[#86EFAC]">"00020126..."</span>,</div>
                <div>{"  "}<span className="text-[#A5B4FC]">"pix_copy_paste"</span>{": "}<span className="text-[#86EFAC]">"00020126..."</span>,</div>
                <div>{"  "}<span className="text-[#A5B4FC]">"expires_at"</span>{": "}<span className="text-[#86EFAC]">"2026-03-29T15:30:00Z"</span>,</div>
                <div>{"  "}<span className="text-[#A5B4FC]">"quote"</span>{": "}<span className="text-white/50">{"{"}</span></div>
                <div>{"    "}<span className="text-[#A5B4FC]">"customer_pays_brl"</span>{": "}<span className="text-[#FDE68A]">100.00</span>,</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"customer_receives_sats"</span>{": "}<span className="text-[#FDE68A]">18302</span>,</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"rate_brl_per_btc"</span>{": "}<span className="text-[#FDE68A]">546350.00</span>,</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"your_spread_pct"</span>{": "}<span className="text-[#FDE68A]">1.5</span>,</div>
                <div>{"    "}<span className="text-[#A5B4FC]">"your_revenue_sats"</span>{": "}<span className="text-[#FDE68A]">278</span></div>
                <div>{"  "}<span className="text-white/50">{"}"}</span></div>
                <div className="text-white/50">{"}"}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
